import React, { useCallback, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { fetchAuthSession } from 'aws-amplify/auth';
import { JobType, StepStatus, StepType } from '../graphql/types';
import type { Document, File as FileType, WorkflowJob } from '../graphql/types';
import * as workflowService from '../services/workflowService';
import { hasOpenedTitleAnalysis } from '../workspace/titleSearchStatus';
import { firstPageOf } from '../workspace/review/reviewModel';
import type { ReviewActions } from '../workspace/review/reviewModel';
import { persistFieldEdit } from '../workspace/review/reviewFinalize';
import { DocumentProcessingStep } from '../components/steps';
import { useTitleSearchWorkspace } from '../workspace/TitleSearchWorkspace';
import { isTitleReviewFinalized } from '../workspace/rowMode';
import IntakeTR from '../workspace/intake/IntakeTR';
import ReviewMode from '../workspace/review/ReviewMode';
import FindingsMode from '../workspace/findings/FindingsMode';
import TitleAnalysisMode from '../workspace/analysis/TitleAnalysisMode';
import { useFinalizeReview } from '../workspace/review/useFinalizeReview';

/**
 * TitleSearchJobPage - legacy Title Search (TR) Mode body hosted inside the
 * Workspace.
 *
 * The Workspace container (`TitleSearchWorkspace`) loads the job, keeps it fresh
 * (`onJobStatusChanged` subscription with a polling fallback), renders the
 * loading/error states, the Top_Bar (title, Mode_Switch, Status_Pill) and the
 * Rail (order summary and steps). This component only receives the job and the
 * active Mode through `useTitleSearchWorkspace()` and renders the existing
 * content for that Mode until the Review, Findings and Title analysis Mode
 * bodies replace it (tasks 7 to 9):
 *
 *   - Intake: `IntakeTR`, read-only for an existing search.
 *   - Review: `ReviewMode` while the review is not finalized (Finalize
 *     persists, unlocks Findings and Title analysis and opens Findings), with
 *     the Document Processing (upload, submit, process) state shown until the
 *     first document is extracted; read-only `ReviewMode` once it is finalized.
 *   - Findings: `FindingsMode` (recorded documents oldest -> newest by recorded
 *     date, or judgment date for judgments, with type tags; property tax card;
 *     call-to-action to open Title analysis).
 *   - Title analysis: run control and report. The Review_Gate keeps Findings
 *     and Title analysis locked until Document Processing is completed.
 *
 * The Property Tax Search and Recorded Document Search agent steps have been
 * temporarily removed from this view; their components/helpers remain in the
 * codebase so they can be restored later.
 */
const TitleSearchJobPage: React.FC = () => {
  const location = useLocation();
  const { job, mode, reload, updateJob, setMode, select } = useTitleSearchWorkspace();
  const { finalize, finalizing, finalizeError } = useFinalizeReview({
    jobId: job.jobId,
    updateJob,
    setMode,
  });

  // Review mode and the upload view are two views of the same step. The
  // reviewer's actions live here so "Back to upload" and returning keep them;
  // `reviewKey` remounts Review mode on return so it loads the new documents.
  const [reviewView, setReviewView] = useState<'review' | 'upload'>('review');
  const [reviewKey, setReviewKey] = useState(0);
  const [reviewActions, setReviewActions] = useState<ReviewActions>({});
  const autoOpenedRef = useRef(false);

  const openReview = useCallback(
    (documentId?: string | null) => {
      setReviewView('review');
      setReviewKey((k) => k + 1);
      if (documentId) select({ doc: documentId, field: null });
    },
    [select]
  );

  // "Open title analysis": Findings is complete, and Title analysis is in
  // progress until an analysis completes. The Mode opens at once; the step
  // status is saved behind it.
  const openAnalysis = useCallback(async () => {
    setMode('analysis');
    if (hasOpenedTitleAnalysis(job)) return;
    updateJob({
      ...job,
      findingsDisplayStatus: 'completed' as WorkflowJob['findingsDisplayStatus'],
    });
    try {
      const session = await fetchAuthSession();
      const tenantId =
        (session.tokens?.idToken?.payload['custom:tenantId'] as string) || 'default-tenant';
      await workflowService.updateStepStatus(
        job.jobId,
        tenantId,
        JobType.TR,
        StepType.FINDINGS_DISPLAY,
        StepStatus.COMPLETED
      );
    } catch (err) {
      console.error('[TitleSearchJobPage] Failed to complete the Findings step:', err);
    }
  }, [job, setMode, updateJob]);

  // "Review data" on a file: open Review mode at that file's first document.
  const reviewFile = useCallback(
    (_file: FileType, documents: Document[]) => {
      const first = [...documents].sort(
        (a, b) => firstPageOf(a.pageRange) - firstPageOf(b.pageRange)
      )[0];
      openReview(first?.documentId);
    },
    [openReview]
  );

  // A file finished processing while the processing state is on screen: move
  // to Review mode without a reload. Once, so it never pulls the reviewer back
  // from the upload view.
  const reviewReady = useCallback(() => {
    if (autoOpenedRef.current) return;
    autoOpenedRef.current = true;
    openReview();
  }, [openReview]);

  // Upload bucket from navigation state or env (mirrors WorkflowJobPage).
  const uploadBucket: string | undefined =
    location.state?.uploadBucket || import.meta.env.VITE_S3_DOCUMENTS_BUCKET;

  const docProcessingDone = isTitleReviewFinalized(job);

  // Defensive: the Review_Gate already locks these Modes, but never render
  // Findings or analysis content for an unfinalized review.
  const showFindings = mode === 'findings' && docProcessingDone;
  const showAnalysis = mode === 'analysis' && docProcessingDone;

  // Review: until the review is finalized, the reviewer works field by field,
  // as in Extract mode. This does not wait for the job-level loan counts: a
  // document appears as soon as it is extracted, and Finalize stays off while a
  // file is processing. Until the first document arrives, the processing state
  // stays on screen (Requirement 6.7).
  if (mode === 'review' && !docProcessingDone) {
    // Upload view (after "Back to upload"): add documents, see processing, and
    // open a processed file with "Review data".
    if (reviewView === 'upload') {
      return (
        <div className="mx-auto max-w-6xl rounded-xl border border-gray-200 bg-white p-8">
          <DocumentProcessingStep
            job={job}
            jobType={JobType.TR}
            onStepComplete={() => void reload()}
            onJobUpdate={updateJob}
            uploadBucket={uploadBucket}
            onReviewFile={reviewFile}
          />
        </div>
      );
    }
    return (
      <ReviewMode
        key={reviewKey}
        job={job}
        uploadBucket={uploadBucket}
        onFinalize={finalize}
        finalizing={finalizing}
        finalizeError={finalizeError}
        actions={reviewActions}
        onActionsChange={setReviewActions}
        onBack={() => setReviewView('upload')}
        emptyState={
          <div className="mx-auto max-w-6xl rounded-xl border border-gray-200 bg-white p-8">
            <DocumentProcessingStep
              job={job}
              jobType={JobType.TR}
              onStepComplete={() => void reload()}
              onJobUpdate={updateJob}
              uploadBucket={uploadBucket}
              onReviewFile={reviewFile}
              onReviewReady={reviewReady}
            />
          </div>
        }
      />
    );
  }

  // A finalized review reopens read-only, with each field's stored action and
  // edited value (Requirement 8.9).
  if (mode === 'review' && docProcessingDone) {
    return (
      <ReviewMode
        job={job}
        uploadBucket={uploadBucket}
        readOnly
        onSaveEdit={persistFieldEdit}
      />
    );
  }

  // Findings: the recorded-document timeline, property tax card and the
  // call-to-action that opens Title analysis (Requirements 10.1 to 10.3).
  if (showFindings) {
    return <FindingsMode job={job} onOpenAnalysis={() => void openAnalysis()} />;
  }

  return (
    <div className="mx-auto max-w-6xl space-y-8">
      {showAnalysis ? (
        /* Title Analysis (Requirements 18.1, 18.2, 18.21). The section reads
           `titleAnalysisStatus` itself, independent of the four-step logic in
           titleSearch/steps. */
        <TitleAnalysisMode job={job} onRunStatusChange={() => void reload()} />
      ) : mode === 'intake' ? (
        /* Intake: read-only search order, subject loan and documents of an
           existing search (Requirement 6). */
        <IntakeTR job={job} />
      ) : (
        /* Document Processing (full upload/submit/process UI) */
        <div className="rounded-xl border border-gray-200 bg-white p-8">
          <DocumentProcessingStep
            job={job}
            jobType={JobType.TR}
            onStepComplete={() => void reload()}
            onJobUpdate={updateJob}
            uploadBucket={uploadBucket}
          />
        </div>
      )}
    </div>
  );
};

export default TitleSearchJobPage;
